import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link2, Copy, ShieldCheck, X, Users } from "lucide-react";
import { toast } from "sonner";

// Liaison parentale par consentement mutuel : le parent génère un code, l'enfant le saisit.
export default function FamilyLink() {
  const { user } = useAuth();
  const minor = user?.is_minor || user?.profile === "child" || user?.profile === "teen";
  const [family, setFamily] = useState({ children: [], guardians: [] });
  const [code, setCode] = useState("");
  const [entry, setEntry] = useState("");

  const load = () => api.get("/family").then(({ data }) => setFamily(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const genCode = async () => {
    try { const { data } = await api.post("/family/link-code"); setCode(data.code); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const redeem = async () => {
    try { await api.post("/family/redeem", { code: entry.trim() }); toast.success("Compte lié à ton parent."); setEntry(""); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const revoke = async (id) => {
    try { await api.post(`/family/revoke/${id}`); toast.success("Liaison retirée."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="card-surface p-5" data-testid="family-link">
      <div className="flex items-center gap-2 mb-3">
        <Users size={18} className="text-gold" />
        <h3 className="font-display text-base" style={{ color: "var(--text-main)" }}>Liaison parent-enfant</h3>
      </div>

      {minor ? (
        <>
          <p className="text-sm mb-3" style={{ color: "color-mix(in srgb, var(--text-main) 70%, transparent)" }}>
            Tu as reçu un code d'un parent ? Saisis-le pour lier ton compte au sien.
          </p>
          <div className="flex gap-2">
            <input data-testid="family-code-input" value={entry} onChange={(e) => setEntry(e.target.value.toUpperCase())}
              placeholder="Code du parent" maxLength={12}
              className="flex-1 rounded-xl px-3 py-2 bg-white/[0.05] border border-white/10 outline-none text-sm" />
            <button data-testid="family-redeem-btn" onClick={redeem} disabled={!entry.trim()}
              className="rounded-xl px-4 py-2 gold-gradient text-sm font-medium disabled:opacity-50" style={{ color: "var(--accent-fg)" }}>Lier</button>
          </div>
          {family.guardians.length > 0 && (
            <ul className="mt-4 space-y-2" data-testid="family-guardians">
              {family.guardians.map((g) => (
                <li key={g.link_id} className="flex items-center justify-between text-sm rounded-xl px-3 py-2 bg-white/[0.04]">
                  <span className="flex items-center gap-2" style={{ color: "var(--text-main)" }}><ShieldCheck size={15} className="text-emerald-400" /> {g.parent_name}</span>
                  <button onClick={() => revoke(g.link_id)} className="text-white/40 hover:text-white" aria-label="Retirer"><X size={16} /></button>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <>
          <p className="text-sm mb-3" style={{ color: "color-mix(in srgb, var(--text-main) 70%, transparent)" }}>
            Générez un code et communiquez-le à votre enfant. Il le saisira depuis son propre compte pour établir la liaison (double consentement).
          </p>
          <button data-testid="family-gen-btn" onClick={genCode}
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2 gold-gradient text-sm font-medium" style={{ color: "var(--accent-fg)" }}>
            <Link2 size={15} /> Générer un code de liaison
          </button>
          {code && (
            <div className="mt-3 flex items-center gap-2" data-testid="family-code">
              <span className="font-display text-2xl tracking-widest px-4 py-2 rounded-xl bg-white/[0.06]" style={{ color: "var(--text-main)" }}>{code}</span>
              <button onClick={() => { navigator.clipboard?.writeText(code); toast.success("Code copié."); }} className="text-white/50 hover:text-white"><Copy size={18} /></button>
              <span className="text-xs text-white/40">valable 48h</span>
            </div>
          )}
          {family.children.length > 0 && (
            <ul className="mt-4 space-y-2" data-testid="family-children">
              {family.children.map((c) => (
                <li key={c.link_id} className="flex items-center justify-between text-sm rounded-xl px-3 py-2 bg-white/[0.04]">
                  <span className="flex items-center gap-2" style={{ color: "var(--text-main)" }}><ShieldCheck size={15} className="text-emerald-400" /> {c.child_name}</span>
                  <button onClick={() => revoke(c.link_id)} className="text-white/40 hover:text-white" aria-label="Retirer"><X size={16} /></button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
